import { Helmet } from 'react-helmet';
import styles from './PKM.module.css';

export default function PKM() {
  const pkmSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: 'Personal Knowledge Management (PKM) with MyPeopleDatabase',
    description:
      'Learn how MyPeopleDatabase acts as a personal knowledge management (PKM) hub for your relationships and people data, and see the roadmap of current and future PKM features.',
    about: [
      'personal knowledge management',
      'PKM',
      'contact management',
      'relationship management',
      'MyPeopleDatabase',
    ],
    keywords: [
      'personal knowledge management',
      'PKM software',
      'PKM for relationships',
      'MyPeopleDatabase PKM',
      'digital address book',
      'contact manager',
    ],
  };

  return (
    <div className={styles.container}>
      <Helmet>
        <title>Personal Knowledge Management (PKM) for Relationships | MyPeopleDatabase</title>
        <meta
          name="description"
          content="See how MyPeopleDatabase becomes the core of your personal knowledge management (PKM) system for people, relationships, and contact history, with current capabilities and future roadmap."
        />
        <meta
          name="keywords"
          content="personal knowledge management, PKM, MyPeopleDatabase, contact manager, digital address book, relationship management"
        />
        <script type="application/ld+json">{JSON.stringify(pkmSchema)}</script>
      </Helmet>

      <div className={styles.overlay} />
      <div className={styles.content}>
        <h1 className={styles.title}>Personal Knowledge Management (PKM) for Your People</h1>
        <p className={styles.lead}>
          MyPeopleDatabase is more than a digital address book. It is the core of your personal
          knowledge management (PKM) system for people, relationships, and the stories that connect
          them.
        </p>

        <section className={styles.section}>
          <h2>How PKM Relates to MyPeopleDatabase</h2>
          <p>
            Traditional PKM tools focus on notes, documents, and tasks. MyPeopleDatabase brings PKM
            principles to your relationships by giving you a structured, searchable home for
            everything you know about the people in your life: contact details, groups, history, and
            context.
          </p>
          <ul className={styles.list}>
            <li>Treat each person as a long-lived knowledge node that you can enrich over time.</li>
            <li>
              Connect people into groups, projects, and families so you see relationships, not just
              rows in a table.
            </li>
            <li>
              Keep ownership of your relationship data instead of giving it to ad-driven platforms.
            </li>
          </ul>
        </section>

        <section className={styles.section}>
          <h2>Current PKM-Oriented Features</h2>
          <ul className={styles.list}>
            <li>
              <strong>Rich contact records:</strong> central place for names, emails, phones, and
              notes about people.
            </li>
            <li>
              <strong>Notes tab (desktop app):</strong> create dated notes per person, add
              comma-separated tags, and quickly filter by tag or text. You can add, update, and
              delete notes as your knowledge evolves.
            </li>
            <li>
              <strong>Groups and categories:</strong> organize contacts into flexible groups that
              match how you think about your network.
            </li>
            <li>
              <strong>Deceased contact handling:</strong> keep a respectful historical record while
              keeping your active list clean.
            </li>
            <li>
              <strong>Local-first storage:</strong> your knowledge about people lives on your
              machine, under your control.
            </li>
            <li>
              <strong>Import/export:</strong> consolidate scattered contact knowledge from other
              systems into one PKM-friendly database.
            </li>
          </ul>
        </section>

        <section className={styles.section}>
          <h2>Planned & Future PKM Features</h2>
          <p>
            The roadmap for MyPeopleDatabase is focused on deepening its role as a PKM hub for
            relationships.
          </p>
          <ul className={styles.list}>
            <li>
              <strong>Timeline & history views (planned):</strong> see key interactions, life
              events, and notes for each person on a unified timeline.
            </li>
            <li>
              <strong>Richer notes and attachments (planned):</strong> attach files, links, and
              reference material to people and groups, and make notes more powerful over time.
            </li>
            <li>
              <strong>Contextual reminders (planned):</strong> gentle prompts to reconnect with
              important people on your own terms.
            </li>
            <li>
              <strong>Deeper group intelligence (planned):</strong> PKM-style insights across
              families, clients, projects, or communities.
            </li>
          </ul>
        </section>

        <section className={styles.section}>
          <h2>Using MyPeopleDatabase in a PKM Stack</h2>
          <p>
            You can pair MyPeopleDatabase with your favorite note-taking or PKM apps (Obsidian,
            Logseq, Notion, and others) by treating it as the single source of truth for people and
            using links, IDs, or exports when you need to reference contacts elsewhere.
          </p>
          <ul className={styles.list}>
            <li>Keep people and relationship data in MyPeopleDatabase.</li>
            <li>Link from notes to stable person IDs or profile URLs.</li>
            <li>Use exports when you want a snapshot of your people data in another tool.</li>
          </ul>
        </section>

        <section className={styles.sectionCallout}>
          <h2>Start Building Your People-Centered PKM System</h2>
          <p>
            If you want your personal knowledge management system to honor the people in your life,
            MyPeopleDatabase gives you a dedicated, privacy-respecting home for that knowledge.
          </p>
          <a href="/features" className={styles.primaryLink}>
            Explore all features
          </a>
        </section>
      </div>
    </div>
  );
}
